import apiClient from '../api/apiClient';

const IFA_ADMIN_BASE = '/api/v1/admin/partners';
const PARTNER_BASE = '/api/v1/partner';

const ifaService = {
  getPartnerProfileSection: async (partnerId, section, params = {}) => {
    const { signal, ...queryParams } = params;
    const response = await apiClient.get(`${IFA_ADMIN_BASE}/${partnerId}`, {
      params: { section, ...queryParams },
      signal,
    });
    return response?.data?.data || {};
  },

  getPartnerSections: async (partnerId, sectionCsv) => {
    const response = await apiClient.get(`${IFA_ADMIN_BASE}/${partnerId}`, {
      params: { section: sectionCsv },
    });
    return response?.data?.data || {};
  },

  savePartnerCommunication: async (partnerId, payload) => {
    const response = await apiClient.post(`${IFA_ADMIN_BASE}/${partnerId}/communication`, payload);
    return response.data;
  },

  updatePartnerOrderCreditSettings: async (partnerId, payload) => {
    const response = await apiClient.post(`${IFA_ADMIN_BASE}/${partnerId}/order-credit-settings`, payload);
    return response?.data;
  },

  updatePartnerMarginCapSettings: async (partnerId, payload) => {
    const response = await apiClient.post(`${IFA_ADMIN_BASE}/${partnerId}/margin-cap-settings`, payload);
    return response?.data?.data || {};
  },

  checkEmail: async (email) => {
    const response = await apiClient.get(`${IFA_ADMIN_BASE}/check-email`, { params: { email } });
    return response.data;
  },

  createIFA: async (email) => {
    const response = await apiClient.post(IFA_ADMIN_BASE, { email });
    return response.data;
  },

  getOnboardingStatus: async (ifaId) => {
    const response = await apiClient.get(`${IFA_ADMIN_BASE}/${ifaId}/onboarding-status`);
    return response.data;
  },

  /** Step 1: verify PAN; backend fetches and sets name */
  updatePan: async (ifaId, data) => {
    const response = await apiClient.patch(`${IFA_ADMIN_BASE}/${ifaId}/pan`, data);
    return response.data;
  },

  /** Step 2: DOB + mobile */
  updatePersonal: async (ifaId, data) => {
    const response = await apiClient.patch(`${IFA_ADMIN_BASE}/${ifaId}/personal`, data);
    return response.data;
  },

  updatePartnerBasicProfile: async (partnerId, payload) => {
    const response = await apiClient.patch(`${IFA_ADMIN_BASE}/${partnerId}/basic-profile`, payload);
    return response.data;
  },

  lookupPAN: async (pan) => {
    const response = await apiClient.post(`${PARTNER_BASE}/investor-onboarding/lookup-pan`, { pan });
    return response.data;
  },

  updateBankDetails: async (ifaId, data) => {
    const response = await apiClient.patch(`${IFA_ADMIN_BASE}/${ifaId}/bank`, data);
    return response.data;
  },

  uploadBankMismatchCheque: async (partnerId, formData) => {
    const response = await apiClient.post(
      `${IFA_ADMIN_BASE}/${partnerId}/bank-mismatch-cheque`,
      formData,
      {
        transformRequest: [
          (data, headers) => {
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
              if (headers?.delete) headers.delete('Content-Type');
              else if (headers) {
                delete headers['Content-Type'];
                delete headers['content-type'];
              }
            }
            return data;
          },
        ],
      }
    );
    return response.data;
  },

  addPartnerBankAccount: async (partnerId, payload) => {
    const response = await apiClient.post(`${IFA_ADMIN_BASE}/${partnerId}/bank-accounts`, payload);
    return response.data;
  },

  setPrimaryBankAccount: async (partnerId, bankUuid) => {
    const response = await apiClient.patch(
      `${IFA_ADMIN_BASE}/${partnerId}/bank-accounts/${bankUuid}/set-primary`
    );
    return response.data;
  },

  deletePartnerBankAccount: async (partnerId, bankUuid) => {
    const response = await apiClient.delete(
      `${IFA_ADMIN_BASE}/${partnerId}/bank-accounts/${bankUuid}`
    );
    return response.data;
  },

  getBankMismatchPendingReview: async (partnerId) => {
    const response = await apiClient.get(
      `${IFA_ADMIN_BASE}/${partnerId}/bank-mismatch/pending-review`
    );
    return response?.data?.data ?? response?.data ?? {};
  },

  verifyBankMismatchDocument: async (partnerId, documentId, status) => {
    const response = await apiClient.patch(
      `${IFA_ADMIN_BASE}/${partnerId}/documents/${encodeURIComponent(documentId)}/verify`,
      { status }
    );
    return response.data;
  },

  /** KYC file review (all types). Do not use for bank-mismatch cheque — that stays on /verify. */
  reviewPartnerDocument: async (partnerId, documentId, payload) => {
    const response = await apiClient.patch(
      `${IFA_ADMIN_BASE}/${partnerId}/documents/${encodeURIComponent(documentId)}/review`,
      payload
    );
    return response.data;
  },

  getStatusChangeChecklist: async (partnerId) => {
    const response = await apiClient.get(`${IFA_ADMIN_BASE}/${partnerId}/status-change`);
    return response.data;
  },

  changePartnerStatus: async (partnerId) => {
    const response = await apiClient.post(`${IFA_ADMIN_BASE}/${partnerId}/status-change`, {});
    return response.data;
  },

  getBankFromMobile: async (mobile, customerName, userId) => {
    const body = { mobile };
    if (customerName) body.customer_name = customerName;
    if (userId) body.user_id = userId;
    const response = await apiClient.post(`${PARTNER_BASE}/onboarding/bank-from-mobile`, body);
    return response.data;
  },

  lookupIFSC: async (ifsc) => {
    const response = await apiClient.post(`${PARTNER_BASE}/onboarding/lookup-ifsc`, { ifsc });
    return response.data;
  },

  uploadDocument: async (ifaId, formData) => {
    const response = await apiClient.post(`${IFA_ADMIN_BASE}/${ifaId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadPartnerDematCmr: async (partnerId, formData) => {
    const response = await apiClient.post(`${IFA_ADMIN_BASE}/${partnerId}/demat/upload-cmr`, formData, {
      transformRequest: [
        (data, headers) => {
          if (typeof FormData !== 'undefined' && data instanceof FormData) {
            if (headers?.delete) headers.delete('Content-Type');
            else if (headers) {
              delete headers['Content-Type'];
              delete headers['content-type'];
            }
          }
          return data;
        },
      ],
    });
    return response.data;
  },

  editDematAccount: async (partnerId, dematUuid, payload) => {
    const response = await apiClient.patch(
      `${IFA_ADMIN_BASE}/${partnerId}/demat-accounts/${dematUuid}`,
      payload
    );
    return response.data;
  },

  sendInvite: async (ifaId) => {
    const response = await apiClient.post(`${IFA_ADMIN_BASE}/${ifaId}/send-invite`);
    return response.data;
  },

  resendInvite: async (partnerId) => {
    const response = await apiClient.post(`${IFA_ADMIN_BASE}/${partnerId}/resend-invite`);
    return response.data;
  },

  assignPricingGroup: async (partnerUuid, payload) => {
    const response = await apiClient.patch(`/api/v1/admin/users/${partnerUuid}/pricing-group`, payload);
    return response?.data;
  },

  generatePartnerPerformanceReport: async (partnerId, { start_date, end_date }) => {
    const response = await apiClient.post(
      `${IFA_ADMIN_BASE}/${partnerId}/performance-report/generate`,
      { start_date, end_date },
      {
        responseType: 'blob',
        timeout: 120000,
      },
    );
    return response.data;
  },

  getAdminIfas: async (params = {}) => {
    const { signal, ...rest } = params;
    const defaultParams = {
      page: 1,
      limit: 10,
      search: '',
      status: 'all',
      sort_by: 'created_at',
      sort_order: 'desc',
      start_date: '',
      end_date: '',
      manager_id: '',
      agreement_status: '',
      ...rest,
    };
    if (!defaultParams.active_in_period) {
      delete defaultParams.active_in_period;
    }
    const response = await apiClient.get(IFA_ADMIN_BASE, { params: defaultParams, signal });
    const body = response.data;
    if (body && typeof body === 'object' && 'data' in body) return body.data;
    return body;
  },
};

export default ifaService;
